import { Archive, Check, ChevronDown, ChevronUp, Loader2, Pencil, Plus, RotateCcw, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { HelpHint } from './HelpHint';

/**
 * A reference list an administrator curates — add, correct, retire, restore.
 *
 * ## Why one component rather than four screens
 *
 * Job types, zones, suppliers, stores and vans are different things with the
 * same shape: rows with a name, a few fields beside it, and four verbs. Written
 * four times they drift — one grows a confirmation dialogue, one greys its
 * button out in silence, one forgets that retiring is reversible and says
 * "Delete" — and the drift is invisible until somebody who learned one screen
 * is surprised by another.
 *
 * Written once, a fix reaches all of them. The reason the stock list had a
 * submit button that did nothing is that it was the only screen of its kind.
 *
 * ## The rules it enforces for every list
 *
 * **Retiring is never deleting**, and the screen says so before the press
 * rather than after. Every one of these rows is named by records that outlive
 * it — a ticket names its job type, a purchase order names its supplier — so a
 * deleted row takes the meaning of those records with it.
 *
 * **A disabled button says why.** `disableWhy` is required alongside any
 * disabled state rather than optional, because "greyed out and silent" is the
 * single most reported fault in this application.
 *
 * **The key is never editable.** Whatever identifies a row to history — a
 * code, an id — is shown and not offered as a field. Renaming must re-file
 * nothing.
 */

export type ManagedField<Row> = {
  /** The property on the row, and the key in the draft. */
  name: string;
  label: string;
  /** Shown under the input. Say what it is for, not what it is. */
  hint?: string;
  /** Longer guidance, in a ⓘ beside the label. */
  help?: ReactNode;
  type?: 'text' | 'number' | 'select';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  required?: boolean;
  /** Only asked when adding. A join key cannot be edited afterwards. */
  addOnly?: boolean;
  /** How it reads on the row itself, when it is not the name. */
  show?: (row: Row) => string | null;
};

export type ManagedListProps<Row extends { id: string }> = {
  title: string;
  /** One sentence under the heading. What this list decides. */
  blurb: string;
  help?: ReactNode;
  fields: Array<ManagedField<Row>>;

  rows: Row[];
  loading?: boolean;
  error?: boolean;

  nameOf: (row: Row) => string;
  keyOf?: (row: Row) => string | null;
  /** What is pointing at it. Shown on the row, and decides the retire button. */
  usageOf?: (row: Row) => { count: number; noun: string } | null;
  retiredOf: (row: Row) => boolean;

  mayEdit: boolean;
  mayRetire: boolean;

  /*
   * These may return a promise, and when they do the form closes on resolve
   * rather than on click.
   *
   * Closing on click loses whatever was typed the moment a save is refused —
   * and a refusal is exactly when somebody needs their input back. Leaving the
   * form open on failure and closing it on success is the only arrangement
   * where the error message and the field it is about are on screen together.
   */
  onAdd: (draft: Record<string, string>) => void | Promise<unknown>;
  onEdit: (id: string, draft: Record<string, string>) => void | Promise<unknown>;
  onRetire: (id: string) => void;
  onRestore: (id: string) => void;
  pending?: boolean;

  outcome?: string | null;
  problem?: string | null;
  onDismiss?: () => void;
  onProblem?: (message: string) => void;

  /** Collapsed until opened, for a list that is not the point of the page. */
  collapsible?: boolean;
  addLabel?: string;
  testId?: string;
};

export function ManagedList<Row extends { id: string }>({
  title,
  blurb,
  help,
  fields,
  rows,
  loading,
  error,
  nameOf,
  keyOf,
  usageOf,
  retiredOf,
  mayEdit,
  mayRetire,
  onAdd,
  onEdit,
  onRetire,
  onRestore,
  pending,
  outcome,
  problem,
  onDismiss,
  onProblem,
  collapsible = false,
  addLabel = 'Add one',
  testId,
}: ManagedListProps<Row>) {
  const [open, setOpen] = useState(!collapsible);
  const [showRetired, setShowRetired] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);

  const live = rows.filter((row) => !retiredOf(row));
  const retired = rows.filter((row) => retiredOf(row));
  const shown = showRetired ? retired : live;

  const blank = () => Object.fromEntries(fields.map((field) => [field.name, '']));

  const startAdding = () => {
    setDraft(blank());
    setAdding(true);
    setEditingId(null);
  };

  const startEditing = (row: Row) => {
    setDraft(
      Object.fromEntries(
        fields.map((field) => {
          const value = (row as unknown as Record<string, unknown>)[field.name];
          return [field.name, value === null || value === undefined ? '' : String(value)];
        }),
      ),
    );
    setEditingId(row.id);
    setAdding(false);
  };

  /** What is still needed, in the words on the form. */
  const missing = (forAdd: boolean) =>
    fields
      .filter((field) => field.required && (forAdd || !field.addOnly))
      .filter((field) => !String(draft[field.name] ?? '').trim())
      .map((field) => field.label.toLowerCase());

  const submit = (forAdd: boolean, id?: string) => {
    const needed = missing(forAdd);
    if (needed.length > 0) {
      onProblem?.(
        `This needs ${needed.length === 1 ? needed[0] : `${needed.slice(0, -1).join(', ')} and ${needed[needed.length - 1]}`} before it can be saved.`,
      );
      return;
    }
    const done = forAdd ? onAdd(draft) : id ? onEdit(id, draft) : undefined;
    const close = () => (forAdd ? setAdding(false) : setEditingId(null));

    if (done && typeof (done as Promise<unknown>).then === 'function') {
      void (done as Promise<unknown>).then(close).catch(() => undefined);
    } else {
      close();
    }
  };

  const inputsFor = (forAdd: boolean) => (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields
        .filter((field) => forAdd || !field.addOnly)
        .map((field) => (
          <label key={field.name} className="block text-sm">
            <span className="mb-1 flex items-center gap-1 font-medium">
              {field.label}
              {field.required && (
                <>
                  <span className="text-accent-600" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </>
              )}
              {field.help && <HelpHint>{field.help}</HelpHint>}
            </span>

            {field.type === 'select' ? (
              <select
                className="input w-full"
                value={draft[field.name] ?? ''}
                onChange={(event) =>
                  setDraft((old) => ({ ...old, [field.name]: event.target.value }))
                }
              >
                <option value="">Choose…</option>
                {(field.options ?? []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="input w-full"
                type={field.type === 'number' ? 'number' : 'text'}
                inputMode={field.type === 'number' ? 'numeric' : undefined}
                value={draft[field.name] ?? ''}
                placeholder={field.placeholder}
                onChange={(event) =>
                  setDraft((old) => ({ ...old, [field.name]: event.target.value }))
                }
              />
            )}

            {field.hint && (
              <span className="mt-1 block text-xs text-muted-foreground">{field.hint}</span>
            )}
          </label>
        ))}
    </div>
  );

  return (
    <section className="card space-y-3" data-managed-list={testId ?? title}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button
          type="button"
          className="flex items-center gap-1.5 text-left font-heading text-lg"
          onClick={() => collapsible && setOpen((was) => !was)}
          aria-expanded={collapsible ? open : undefined}
        >
          {title}
          {collapsible &&
            (open ? (
              <ChevronUp className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ))}
        </button>

        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {blurb}
          {help && <HelpHint>{help}</HelpHint>}
        </span>
      </div>

      {open && (
        <>
          {outcome && (
            <p className="flex items-start justify-between gap-3 rounded-xl border border-secondary-300 bg-secondary-50 p-3 text-sm text-secondary-900">
              <span>{outcome}</span>
              {onDismiss && (
                <button type="button" onClick={onDismiss} aria-label="Dismiss">
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </p>
          )}

          {problem && (
            <p className="rounded-xl border border-accent-300 bg-accent-50 p-3 text-sm text-accent-900">
              {problem}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {mayEdit && !adding && !showRetired && (
              <button type="button" className="btn-secondary" onClick={startAdding}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {addLabel}
              </button>
            )}

            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => {
                setShowRetired((was) => !was);
                setAdding(false);
                setEditingId(null);
              }}
            >
              <Archive className="h-4 w-4" aria-hidden="true" />
              {showRetired ? `Back to the list (${live.length})` : `Retired (${retired.length})`}
            </button>
          </div>

          {adding && (
            <div className="space-y-3 rounded-xl border border-border p-3">
              {inputsFor(true)}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={pending}
                  onClick={() => submit(true)}
                >
                  {pending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  )}
                  Add it
                </button>
                <button type="button" className="btn-secondary" onClick={() => setAdding(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {loading && <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-primary-500" />}

          {error && (
            <p className="text-sm text-muted-foreground">
              This list could not be loaded. Reload the page, and if it keeps happening tell an
              administrator.
            </p>
          )}

          {!loading && shown.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {showRetired
                ? 'Nothing has been retired. Anything you retire lands here and can be brought back.'
                : 'Nothing on this list yet.'}
            </p>
          )}

          <ul className="divide-y divide-border">
            {shown.map((row) => {
              const usage = usageOf?.(row) ?? null;
              const isRetired = retiredOf(row);

              return (
                <li key={row.id} className="py-2.5">
                  {editingId === row.id ? (
                    <div className="space-y-3">
                      {inputsFor(false)}
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="btn-primary py-1.5 text-sm"
                          disabled={pending}
                          onClick={() => submit(false, row.id)}
                        >
                          <Check className="h-4 w-4" aria-hidden="true" />
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn-secondary py-1.5 text-sm"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2 font-medium">
                          {nameOf(row)}
                          {keyOf?.(row) && (
                            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-xs font-normal text-muted-foreground">
                              {keyOf(row)}
                            </span>
                          )}
                          {isRetired && (
                            <span className="rounded-full bg-accent-50 px-2 py-0.5 text-xs font-normal text-accent-800">
                              retired
                            </span>
                          )}
                        </p>

                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {fields
                            .map((field) => field.show?.(row))
                            .filter(Boolean)
                            .join(' · ')}
                        </p>

                        {usage && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {usage.count === 0
                              ? `Nothing uses it yet`
                              : `${usage.count} ${usage.count === 1 ? usage.noun : `${usage.noun}s`}`}
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 items-center gap-1">
                        {!isRetired && mayEdit && (
                          <button
                            type="button"
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                            title={`Correct ${nameOf(row)}`}
                            onClick={() => startEditing(row)}
                          >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Correct {nameOf(row)}</span>
                          </button>
                        )}

                        {!isRetired && mayRetire && (
                          <button
                            type="button"
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                            /*
                             * Never disabled for "it is in use". The server
                             * decides whether that means retire or delete, and
                             * says which — a screen that guesses gets it wrong
                             * in exactly the cases that matter.
                             */
                            title={
                              usage && usage.count > 0
                                ? `Retire ${nameOf(row)} — its ${usage.count} ${usage.count === 1 ? usage.noun : `${usage.noun}s`} stay exactly as they are`
                                : `Remove ${nameOf(row)} — nothing uses it`
                            }
                            disabled={pending}
                            onClick={() => onRetire(row.id)}
                          >
                            <Archive className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Retire {nameOf(row)}</span>
                          </button>
                        )}

                        {isRetired && mayRetire && (
                          <button
                            type="button"
                            className="btn-secondary py-1.5 text-sm"
                            disabled={pending}
                            onClick={() => onRestore(row.id)}
                          >
                            <RotateCcw className="h-4 w-4" aria-hidden="true" />
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {!mayEdit && (
            <p className="text-xs text-muted-foreground">
              Read-only for your role. An administrator can change this list.
            </p>
          )}
        </>
      )}
    </section>
  );
}
